import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { User, Phone, Mail, Globe, MapPin, Edit, Save, FileText, Upload, Download, Trash2, Shield, Briefcase, Heart, Building2, Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import VisitingCard from '../components/VisitingCard';

const DOC_TYPES = [
    { value: 'aadhar', label: 'Aadhar Card' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'resume', label: 'Resume / CV' },
    { value: 'education_10th', label: '10th Marksheet' },
    { value: 'education_12th', label: '12th Marksheet' },
    { value: 'education_degree', label: 'Degree Certificate' },
    { value: 'experience_letter', label: 'Experience Letter' },
    { value: 'relieving_letter', label: 'Relieving Letter' },
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'contract', label: 'Employment Contract' },
    { value: 'bank_proof', label: 'Bank Passbook / Statement' },
    { value: 'photo', label: 'Passport Photo' },
    { value: 'other', label: 'Other' },
];

const Profile = () => {
    const { user, checkAuth } = useAuth();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState('aadhar');
    const fileRef = useRef(null);
    const [profile, setProfile] = useState({
        phone: '', address: '', city: '', state: '', date_of_birth: '', gender: '',
        emergency_contact_name: '', emergency_contact_phone: '', blood_group: '',
        bank_account_number: '', bank_name: '', bank_ifsc: ''
    });

    // Change Password
    const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
    const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
    const [pwdSaving, setPwdSaving] = useState(false);
    const [pwdError, setPwdError] = useState('');

    const userId = user?._id || user?.id;

    const fetchData = useCallback(async () => {
        try {
            const [statusRes, docsRes] = await Promise.all([
                axios.get(`${API_URL}/api/self-onboarding/status`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees/${userId}/documents`, { withCredentials: true })
            ]);
            setStatus(statusRes.data);
            setDocuments(docsRes.data);
            if (statusRes.data.profile) {
                setProfile(prev => ({ ...prev, ...Object.fromEntries(Object.entries(statusRes.data.profile).filter(([_, v]) => v)) }));
            }
        } catch (error) { logger.error('Profile fetch error:', error); }
    }, [userId]);

    useEffect(() => { if (userId) fetchData(); }, [fetchData, userId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/self-onboarding/profile`, profile, { withCredentials: true });
            await fetchData();
            await checkAuth();
            setEditing(false);
        } catch (error) { alert('Error saving profile'); }
        finally { setSaving(false); }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdError('');
        if (pwdForm.next.length < 6) { setPwdError('New password must be at least 6 characters'); return; }
        if (pwdForm.next !== pwdForm.confirm) { setPwdError('New passwords do not match'); return; }
        if (pwdForm.next === pwdForm.current) { setPwdError('New password must differ from current password'); return; }
        setPwdSaving(true);
        try {
            await axios.post(`${API_URL}/api/auth/change-password`,
                { current_password: pwdForm.current, new_password: pwdForm.next },
                { withCredentials: true });
            toast.success('Password changed! Please log in with your new password.');
            setPwdDialogOpen(false);
            setPwdForm({ current: '', next: '', confirm: '' });
            // Backend cleared cookies; force reload to login
            setTimeout(() => { window.location.href = '/login'; }, 1500);
        } catch (err) {
            logger.error('Change password failed', err);
            setPwdError(err?.response?.data?.detail || 'Could not change password');
        } finally {
            setPwdSaving(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/api/employees/${userId}/documents?doc_type=${docType}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            await fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const handleDownload = async (docId, filename) => {
        try {
            const response = await axios.get(`${API_URL}/api/documents/${docId}/download`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) { alert('Download failed'); }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await axios.delete(`${API_URL}/api/documents/${docId}`, { withCredentials: true });
            await fetchData();
        } catch (error) { alert('Delete failed'); }
    };

    return (
        <div className="space-y-6" data-testid="profile-page">
            {/* Visiting Card */}
            <div className="max-w-3xl" data-testid="profile-card">
                <VisitingCard
                    rm={{
                        name: user?.name,
                        phone: profile.phone,
                        email: user?.email,
                        employee_id: user?.employee_id,
                        designation: user?.role === 'admin' ? 'Administrator' : user?.role === 'hr_manager' ? 'HR Manager' : (user?.designation || user?.department || 'Relationship Manager'),
                    }}
                />
            </div>

            {/* Edit Profile Section */}
            <Card className="rounded-none border-slate-200 max-w-2xl">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><User className="h-4 w-4 text-brand" />Personal Details</h3>
                        <Button size="sm" variant={editing ? "default" : "outline"} className="rounded-none h-7 text-xs" onClick={() => editing ? handleSave() : setEditing(true)} disabled={saving} data-testid="edit-profile-btn">
                            {editing ? <><Save className="h-3 w-3 mr-1" />{saving ? 'Saving...' : 'Save'}</> : <><Edit className="h-3 w-3 mr-1" />Edit</>}
                        </Button>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</Label>
                                {editing ? <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="rounded-none mt-1 h-9" data-testid="profile-phone" />
                                : <p className="text-sm text-slate-700 mt-1">{profile.phone || '-'}</p>}</div>
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Date of Birth</Label>
                                {editing ? <Input type="date" value={profile.date_of_birth} onChange={(e) => setProfile({...profile, date_of_birth: e.target.value})} className="rounded-none mt-1 h-9" />
                                : <p className="text-sm text-slate-700 mt-1">{profile.date_of_birth || '-'}</p>}</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Gender</Label>
                                {editing ? <Select value={profile.gender || "not_set"} onValueChange={(v) => setProfile({...profile, gender: v === "not_set" ? "" : v})}>
                                    <SelectTrigger className="rounded-none mt-1 h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent className="rounded-none"><SelectItem value="not_set">Select</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                                </Select> : <p className="text-sm text-slate-700 mt-1 capitalize">{profile.gender || '-'}</p>}</div>
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Blood Group</Label>
                                {editing ? <Select value={profile.blood_group || "not_set"} onValueChange={(v) => setProfile({...profile, blood_group: v === "not_set" ? "" : v})}>
                                    <SelectTrigger className="rounded-none mt-1 h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent className="rounded-none"><SelectItem value="not_set">Select</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
                                </Select> : <p className="text-sm text-slate-700 mt-1">{profile.blood_group || '-'}</p>}</div>
                        </div>
                        <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Address</Label>
                            {editing ? <Input value={profile.address} onChange={(e) => setProfile({...profile, address: e.target.value})} className="rounded-none mt-1 h-9" />
                            : <p className="text-sm text-slate-700 mt-1">{profile.address || '-'}</p>}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">City</Label>
                                {editing ? <Input value={profile.city} onChange={(e) => setProfile({...profile, city: e.target.value})} className="rounded-none mt-1 h-9" />
                                : <p className="text-sm text-slate-700 mt-1">{profile.city || '-'}</p>}</div>
                            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">State</Label>
                                {editing ? <Input value={profile.state} onChange={(e) => setProfile({...profile, state: e.target.value})} className="rounded-none mt-1 h-9" />
                                : <p className="text-sm text-slate-700 mt-1">{profile.state || '-'}</p>}</div>
                        </div>

                        {/* Emergency Contact */}
                        <div className="pt-3 border-t border-slate-100">
                            <p className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1"><Heart className="h-3 w-3 text-red-500" />Emergency Contact</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Name</Label>
                                    {editing ? <Input value={profile.emergency_contact_name} onChange={(e) => setProfile({...profile, emergency_contact_name: e.target.value})} className="rounded-none mt-1 h-9" />
                                    : <p className="text-sm text-slate-700 mt-1">{profile.emergency_contact_name || '-'}</p>}</div>
                                <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</Label>
                                    {editing ? <Input value={profile.emergency_contact_phone} onChange={(e) => setProfile({...profile, emergency_contact_phone: e.target.value})} className="rounded-none mt-1 h-9" />
                                    : <p className="text-sm text-slate-700 mt-1">{profile.emergency_contact_phone || '-'}</p>}</div>
                            </div>
                        </div>

                        {/* Bank Details */}
                        <div className="pt-3 border-t border-slate-100">
                            <p className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1"><Building2 className="h-3 w-3 text-brand" />Bank Details</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Bank Name</Label>
                                    {editing ? <Input value={profile.bank_name} onChange={(e) => setProfile({...profile, bank_name: e.target.value})} className="rounded-none mt-1 h-9" />
                                    : <p className="text-sm text-slate-700 mt-1">{profile.bank_name || '-'}</p>}</div>
                                <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Account No.</Label>
                                    {editing ? <Input value={profile.bank_account_number} onChange={(e) => setProfile({...profile, bank_account_number: e.target.value})} className="rounded-none mt-1 h-9" />
                                    : <p className="text-sm text-slate-700 mt-1">{profile.bank_account_number || '-'}</p>}</div>
                                <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">IFSC</Label>
                                    {editing ? <Input value={profile.bank_ifsc} onChange={(e) => setProfile({...profile, bank_ifsc: e.target.value})} className="rounded-none mt-1 h-9" />
                                    : <p className="text-sm text-slate-700 mt-1">{profile.bank_ifsc || '-'}</p>}</div>
                            </div>
                        </div>
                        {editing && <div className="flex justify-end gap-2 pt-3"><Button variant="outline" size="sm" className="rounded-none" onClick={() => setEditing(false)}>Cancel</Button><Button size="sm" className="bg-brand hover:bg-brand rounded-none" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save Changes'}</Button></div>}
                    </div>
                </CardContent>
            </Card>

            {/* Change Password Section */}
            <Card className="rounded-none border-slate-200 max-w-2xl" data-testid="profile-change-password">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                            <KeyRound className="h-4 w-4 text-brand" />Account Security
                        </h3>
                    </div>
                    <div className="px-5 py-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Lock className="h-5 w-5 text-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-900">Password</p>
                            <p className="text-xs text-slate-500">Change the password used to log in to your account</p>
                        </div>
                        <Dialog open={pwdDialogOpen} onOpenChange={(v) => { setPwdDialogOpen(v); if (!v) { setPwdForm({ current: '', next: '', confirm: '' }); setPwdError(''); } }}>
                            <DialogTrigger asChild>
                                <Button className="bg-brand hover:bg-brand text-white rounded-lg" data-testid="open-change-password-btn">
                                    <KeyRound className="h-3.5 w-3.5 mr-1.5" />Change Password
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-xl max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        <KeyRound className="h-5 w-5 text-brand" />Change Your Password
                                    </DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Current Password</Label>
                                        <Input type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} placeholder="Enter your current password" required autoComplete="current-password" className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="pwd-current-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">New Password</Label>
                                        <Input type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} placeholder="At least 6 characters" required minLength={6} autoComplete="new-password" className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="pwd-new-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm New Password</Label>
                                        <Input type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} placeholder="Re-type new password" required minLength={6} autoComplete="new-password" className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="pwd-confirm-input" />
                                    </div>
                                    {pwdError && (
                                        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded-lg text-xs" data-testid="pwd-error">{pwdError}</div>
                                    )}
                                    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-[11px]">
                                        ℹ️ After changing, you'll be signed out and need to log in again with your new password.
                                    </div>
                                    <div className="flex justify-end gap-2 pt-1">
                                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
                                        <Button type="submit" disabled={pwdSaving} className="bg-brand hover:bg-brand text-white rounded-lg" data-testid="pwd-submit-btn">
                                            {pwdSaving ? 'Changing…' : 'Change Password'}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardContent>
            </Card>

            {/* Documents Section */}
            <Card className="rounded-none border-slate-200 max-w-2xl" data-testid="profile-documents">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><FileText className="h-4 w-4 text-brand" />My Documents</h3>
                        <div className="flex items-center gap-2">
                            <Select value={docType} onValueChange={setDocType}>
                                <SelectTrigger className="w-[160px] rounded-none h-7 text-xs" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none max-h-60">
                                    {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button size="sm" className="bg-brand hover:bg-brand rounded-none h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn">
                                <Upload className="h-3 w-3 mr-1" />{uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                            <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv,.xlsx" />
                        </div>
                    </div>
                    <div>
                        {documents.length === 0 ? (
                            <div className="py-8 text-center text-slate-500">
                                <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm">No documents uploaded yet</p>
                                <p className="text-xs text-slate-400 mt-1">Upload your KYC, education proofs, CV and other documents</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {documents.map(doc => (
                                    <div key={doc._id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50" data-testid={`doc-${doc._id}`}>
                                        <FileText className="h-4 w-4 text-brand flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                                            <p className="text-[10px] text-slate-400">{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type} - {new Date(doc.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-none" onClick={() => handleDownload(doc._id, doc.original_filename)}><Download className="h-3.5 w-3.5" /></Button>
                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-none text-red-600" onClick={() => handleDelete(doc._id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Profile;
